import clsx from 'clsx'
import { LinearGradient, type LinearGradientProps } from 'expo-linear-gradient'
import { useEffect } from 'react'
import { View, StyleSheet } from 'react-native'
import Animated, { Easing, interpolate, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated'

import { assignAnimatedValue } from '@/src/general/utils/assignAnimatedValue'
import { colors } from '@/src/styles/colors'

type TProgressBarFill = string | LinearGradientProps

type TProgressBarProps = {
  progress?: number
  className?: string
  testID?: string
  fill?: TProgressBarFill
}

const defaultFillGradientColors = [colors.danger['bg-inverse'].dark, colors.danger['bg-inverse'].DEFAULT] as const
const defaultFill: LinearGradientProps = {
  colors: defaultFillGradientColors,
  locations: [0.1, 1],
}

const ProgressBarFill = ({ fill }: { fill: TProgressBarFill }) => {
  if (typeof fill === 'string') {
    return <View style={[styles.fill, { backgroundColor: fill }]} className="rounded-full" />
  }

  return (
    <LinearGradient
      className="rounded-full"
      style={styles.fill}
      colors={fill.colors}
      locations={fill.locations}
      start={fill.start}
      end={fill.end}
    />
  )
}

const useProgressBarAnimation = (progress: number) => {
  const animatedProgress = useSharedValue(progress / 100)
  const animatedStyle = useAnimatedStyle(() => ({
    width: `${interpolate(animatedProgress.value, [0, 1], [0, 100])}%`,
  }))
  useEffect(() => {
    assignAnimatedValue(
      animatedProgress,
      withTiming(progress / 100, { duration: 1000, easing: Easing.bezier(0.25, 0.1, 0.25, 1) }),
    )
  }, [progress, animatedProgress])
  return { animatedStyle }
}

export const ProgressBar = ({
  progress = 0,
  className = '',
  testID = 'progress-bar',
  fill = defaultFill,
}: TProgressBarProps) => {
  const { animatedStyle } = useProgressBarAnimation(progress)

  return (
    <View testID={testID} className={clsx('h-8 max-w-full overflow-hidden  shadow-md rounded-full', className)}>
      <Animated.View testID={`${testID}-animated`} style={[animatedStyle, { flex: 1 }]}>
        <ProgressBarFill fill={fill} />
      </Animated.View>
    </View>
  )
}

const styles = StyleSheet.create({
  fill: {
    flex: 1,
  },
})
